import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Body2, Button, Card, Header3, SkeletonLoader } from "@activecollab/components";
import { Divider } from "~/components/ModalObjects";
import { Page } from "~/components/Page";
import { useIntegration } from "~/repository";
import Angie from "~/utils/angie";
import { sortCompare } from "~/utils/utils";
import { IntegrationsTable } from "./IntegrationsTable";
import { WebhookFormDialog } from "./WebhookFormDialog";
import { WebhookIntegrationTableRow } from "./WebhookIntegrationTableRow";

const WebhooksIntegrationsPage = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const { t } = useTranslation();
  const { data, isLoading } = useIntegration("webhooks", {
    select: (response) => {
      if (response && response.single) {
        return response.single.webhooks.sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));
      }
      return [];
    },
  });

  const onOpenDialog = useCallback(() => setOpenDialog(true), [setOpenDialog]);
  const onCloseDialog = useCallback(() => setOpenDialog(false), [setOpenDialog]);

  return (
    <Page
      title="Webhooks"
      className="tw-max-w-[850px] tw-pb-4"
    >
      <Page.Header
        title={t("Webhooks")}
        className="print:tw-hidden"
        backTitle={t("Apps & Integrations")}
        backUrl="/integrations"
      />
      <Card
        variant="paper-2"
        className="tw-px-8 tw-py-6 tw-h-full"
      >
        <div className="tw-flex tw-flex-1 sm:tw-gap-[60px] tw-gap-8 tw-mb-1 sm:tw-flex-row tw-flex-col-reverse">
          <div className="tw-flex tw-flex-1 tw-flex-col">
            <Header3>{t("Notify 3rd party services about what's happening in ActiveCollab")}</Header3>
            <Body2 className="tw-mt-4">
              {t(
                "The URL-s that you specify here will be triggered by the POST HTTP request every time you add a new project, discussion, comment, note, company, user, or file in ActiveCollab."
              )}
            </Body2>
          </div>
          <div className="tw-w-[80px] tw-h-[80px] tw-flex tw-justify-end tw-items-end">
            <img
              src={`${Angie.assets_url}/system/images/integrations/webhooks.png`}
              alt="Webhooks Integration"
              width={69}
              height={64}
            />
          </div>
        </div>
        <Divider />
        <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-4">
          <Header3>{t("Custom Webhooks")}</Header3>

          {isLoading ? (
            <div className="tw-flex tw-flex-col tw-gap-4">
              <SkeletonLoader data-testid="loader" />
              <SkeletonLoader />
              <SkeletonLoader />
            </div>
          ) : data && data.length ? (
            <IntegrationsTable
              columns={[t("Name"), t("URL"), t("Status")]}
              addBtnText={t("Add New Webhook")}
              onAddClick={onOpenDialog}
            >
              {data.map((webhook) => {
                return (
                  <WebhookIntegrationTableRow
                    key={webhook.id}
                    integration={webhook}
                  />
                );
              })}
            </IntegrationsTable>
          ) : (
            <div className="tw-flex tw-flex-col  tw-gap-4">
              <Body2>{t("There aren't any webhooks configured yet.")}</Body2>
              <Button
                className="tw-w-fit"
                onClick={onOpenDialog}
              >
                {t("Add New Webhook")}
              </Button>
            </div>
          )}
        </div>
        <WebhookFormDialog
          open={openDialog}
          onClose={onCloseDialog}
        />
      </Card>
    </Page>
  );
};

export default WebhooksIntegrationsPage;
